import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchUsers,
  deleteUser,
  updateUser,
  fetchInstructors,
  deleteInstructor,
  updateInstructor,
  createInstructor,
  fetchLocations,
  deleteLocation,
  updateLocation,
  createLocation,
  fetchTypes,
  deleteType,
  updateType,
  createType,
  fetchClasses,
  fetchClassesList,
  fetchClassesForAttendance,
  fetchClassesDropdown,
  fetchClass,
  deleteClass,
  updateClass,
  createClass,
  fetchSchedules,
  deleteSchedule,
  createSchedule,
  updateSchedule,
  bulkCreateSchedules,
  bulkUpdateSchedules,
  bulkDeleteSchedules,
  fetchAttendees,
  deleteAttendee,
  createAttendee,
  updateAttendee,
  adjustAttendeeClasses,
  fetchAttendeesForUser,
  getAttendeesToSchedule,
  uploadClassImages,
  deleteClassImage,
  uploadTypeImage,
  deleteTypeImage,
  uploadSubtypeImage,
  deleteSubtypeImage,
  setClassInProgressOfCancellation,
  reactivateClass,
  getClassReport,
  getInstructorClassHoursReport,
  getEnrollmentsReport,
  deactivateSchedule,
  cancelScheduleDeactivation,
  recreateSchedule,
  archiveClass,
  unarchiveClass,
  fetchArchivedClasses,
  getArchivedClass,
  fetchPrices,
  createPrice,
  updatePrice,
  deletePrice,
  fetchPaymentHistory,
  fetchPaymentStatistics,
} from "./adminOperations";
import {
  AdminState,
  Location,
  Attendee,
  Type,
  Schedule,
  User,
  Instructor,
  Class,
  Price,
} from "../types/types";

const initialState: AdminState = {
  users: [],
  totalUsers: 0,
  instructors: [],
  totalInstructors: 0,
  locations: [],
  totalLocations: 0,
  types: [],
  totalTypes: 0,
  classes: [],
  totalClasses: 0,
  classesList: [],
  totalClassesList: 0,
  classesAttendance: [],
  totalClassesAttendance: 0,
  classesDropdown: [],
  archivedClasses: [],
  totalArchivedClasses: 0,
  schedules: [],
  totalSchedules: 0,
  attendees: [],
  totalAttendees: 0,
  loading: false,
  error: null,
  prices: [],
  paymentHistory: [],
  totalPaymentHistory: 0,
  paymentStatistics: null,
  reports: {
    attendeesReports: null,
    enrollmentsReport: null,
    instructorClassHoursReport: null,
  },
};

const appendImagesToClassCollection = (
  classes: Class[],
  classId: number,
  imageUrls: string[]
) => {
  const classIndex = classes.findIndex((c) => c.id === classId);
  if (classIndex === -1) {
    return;
  }

  if (classes[classIndex].images) {
    classes[classIndex].images = [
      ...classes[classIndex].images!,
      ...imageUrls,
    ];
    return;
  }

  classes[classIndex].images = imageUrls;
};

const removeImageFromClassCollection = (
  classes: Class[],
  classId: number,
  imageUrl: string
) => {
  const classIndex = classes.findIndex((c) => c.id === classId);
  if (classIndex === -1 || !classes[classIndex].images) {
    return;
  }

  classes[classIndex].images = classes[classIndex].images!.filter(
    (url) => url !== imageUrl
  );
};

const setImageInTypeCollection = (
  types: Type[],
  typeId: number,
  imageUrl: string
) => {
  const typeIndex = types.findIndex((type) => type.id === typeId);
  if (typeIndex === -1) {
    return;
  }

  types[typeIndex].image = imageUrl;
};

const clearImageFromTypeCollection = (
  types: Type[],
  typeId: number,
  imageUrl: string
) => {
  const typeIndex = types.findIndex((type) => type.id === typeId);
  if (typeIndex === -1 || types[typeIndex].image !== imageUrl) {
    return;
  }

  types[typeIndex].image = null;
};

const setImageInSubtypeCollection = (
  types: Type[],
  subtypeId: number,
  imageUrl: string
) => {
  const type = types.find((item) =>
    item.subtypes.some((subtype) => subtype.id === subtypeId)
  );

  if (!type) {
    return;
  }

  const subtype = type.subtypes.find((item) => item.id === subtypeId);
  if (!subtype) {
    return;
  }

  subtype.image = imageUrl;
};

const clearImageFromSubtypeCollection = (
  types: Type[],
  subtypeId: number,
  imageUrl: string
) => {
  const type = types.find((item) =>
    item.subtypes.some((subtype) => subtype.id === subtypeId)
  );

  if (!type) {
    return;
  }

  const subtype = type.subtypes.find((item) => item.id === subtypeId);
  if (!subtype || subtype.image !== imageUrl) {
    return;
  }

  subtype.image = null;
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    updateUnEnroll(state, action) {
      state.classes.map((c) => {
        if (c.schedules && c.id === action.payload.classId) {
          action.payload.schedules.forEach((i: number) => {
            const schedule = c.schedules.find((s) => s.id === i);
            if (schedule?.attendees) {
              schedule.attendees = schedule.attendees.filter(
                (a) => a.id !== action.payload.attendeeId
              );
            }
            if (schedule?.AttendeeToSchedule) {
              schedule.AttendeeToSchedule = schedule.AttendeeToSchedule.filter(
                (a) => a.attendee.id !== action.payload.attendeeId
              );
            }
            c.schedules.filter((s) => s.id !== i);
            if (schedule) c.schedules.push(schedule);
          });
        }
        return c;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUsers.fulfilled,
        (state, action: PayloadAction<{ users: User[]; total: number }>) => {
          state.users = action.payload.users;
          state.totalUsers = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<number>) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
        state.totalUsers -= 1;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })

      // Instructors
      .addCase(fetchInstructors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchInstructors.fulfilled,
        (
          state,
          action: PayloadAction<{ instructors: Instructor[]; total: number }>
        ) => {
          state.instructors = action.payload.instructors;
          state.totalInstructors = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchInstructors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch instructors";
      })
      .addCase(
        deleteInstructor.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.instructors = state.instructors.filter(
            (instructor) => instructor.id !== action.payload
          );
          state.totalInstructors -= 1;
        }
      )
      .addCase(
        updateInstructor.fulfilled,
        (state, action: PayloadAction<Instructor>) => {
          const index = state.instructors.findIndex(
            (instructor) => instructor.id === action.payload.id
          );
          if (index !== -1) {
            state.instructors[index] = action.payload;
          }
        }
      )
      .addCase(
        createInstructor.fulfilled,
        (state, action: PayloadAction<Instructor>) => {
          state.instructors.push(action.payload);
          state.totalInstructors += 1;
        }
      )

      // Locations
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchLocations.fulfilled,
        (
          state,
          action: PayloadAction<{ locations: Location[]; total: number }>
        ) => {
          state.locations = action.payload.locations;
          state.totalLocations = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch locations";
      })
      .addCase(
        deleteLocation.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.locations = state.locations.filter(
            (location) => location.id !== action.payload
          );
          state.totalLocations -= 1;
        }
      )
      .addCase(
        updateLocation.fulfilled,
        (state, action: PayloadAction<Location>) => {
          const index = state.locations.findIndex(
            (location) => location.id === action.payload.id
          );
          if (index !== -1) {
            state.locations[index] = action.payload;
          }
        }
      )
      .addCase(
        createLocation.fulfilled,
        (state, action: PayloadAction<Location>) => {
          state.locations.push(action.payload);
          state.totalLocations += 1;
        }
      )

      // Types
      .addCase(fetchTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTypes.fulfilled,
        (state, action: PayloadAction<{ types: Type[]; total: number }>) => {
          state.types = action.payload.types;
          state.totalTypes = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch types";
      })
      .addCase(deleteType.fulfilled, (state, action: PayloadAction<number>) => {
        state.types = state.types.filter((type) => type.id !== action.payload);
        state.totalTypes -= 1;
      })
      .addCase(updateType.fulfilled, (state, action: PayloadAction<Type>) => {
        const index = state.types.findIndex(
          (type) => type.id === action.payload.id
        );
        if (index !== -1) {
          state.types[index] = action.payload;
        }
      })
      .addCase(createType.fulfilled, (state, action: PayloadAction<Type>) => {
        state.types.push(action.payload);
        state.totalTypes += 1;
      })

      // Classes
      .addCase(fetchClasses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchClasses.fulfilled,
        (state, action: PayloadAction<{ classes: Class[]; total: number }>) => {
          state.classes = action.payload.classes;
          state.totalClasses = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchClasses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch classes";
      })
      .addCase(fetchClassesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchClassesList.fulfilled,
        (state, action: PayloadAction<{ classes: Class[]; total: number }>) => {
          state.classesList = action.payload.classes;
          state.totalClassesList = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchClassesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch classes list";
      })
      .addCase(fetchClassesForAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchClassesForAttendance.fulfilled,
        (state, action: PayloadAction<{ classes: Class[]; total: number }>) => {
          state.classesAttendance = action.payload.classes;
          state.totalClassesAttendance = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchClassesForAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch classes for attendance";
      })
      .addCase(fetchClassesDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchClassesDropdown.fulfilled,
        (state, action: PayloadAction<{ id: number; title: any }[]>) => {
          state.classesDropdown = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchClassesDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch classes dropdown";
      })
      .addCase(
        deleteClass.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.classes = state.classes.filter(
            (classItem) => classItem.id !== action.payload
          );
          state.classesList = state.classesList.filter(
            (classItem) => classItem.id !== action.payload
          );
          state.totalClasses -= 1;
          state.totalClassesList -= 1;
        }
      )
      .addCase(updateClass.fulfilled, (state, action: PayloadAction<Class>) => {
        const index = state.classes.findIndex(
          (classItem) => classItem.id === action.payload.id
        );
        if (index !== -1) {
          state.classes[index] = action.payload;
        }
        const listIndex = state.classesList.findIndex(
          (classItem) => classItem.id === action.payload.id
        );
        if (listIndex !== -1) {
          state.classesList[listIndex] = action.payload;
        }
      })
      .addCase(createClass.fulfilled, (state, action: PayloadAction<Class>) => {
        state.classes.push(action.payload);
        state.classesList.push(action.payload);
        state.totalClasses += 1;
        state.totalClassesList += 1;
      })
      .addCase(fetchClass.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClass.fulfilled, (state, action: PayloadAction<Class>) => {
        state.loading = false;
        // Update the class in the classes array if it exists, otherwise don't add it
        // since we're just refreshing a single class
        const index = state.classes.findIndex(
          (classItem) => classItem.id === action.payload.id
        );
        if (index !== -1) {
          state.classes[index] = action.payload;
        }
        const listIndex = state.classesList.findIndex(
          (classItem) => classItem.id === action.payload.id
        );
        if (listIndex !== -1) {
          state.classesList[listIndex] = action.payload;
        }
      })
      .addCase(fetchClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch class";
      })

      // Schedules
      .addCase(fetchSchedules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSchedules.fulfilled,
        (
          state,
          action: PayloadAction<{ schedules: Schedule[]; total: number }>
        ) => {
          state.schedules = action.payload.schedules;
          state.totalSchedules = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch schedules";
      })
      .addCase(
        deleteSchedule.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.schedules = state.schedules.filter(
            (schedule) => schedule.id !== action.payload
          );
          state.totalSchedules -= 1;
        }
      )
      .addCase(
        createSchedule.fulfilled,
        (state, action: PayloadAction<Schedule>) => {
          state.schedules.push(action.payload);
          state.totalSchedules += 1;
        }
      )
      .addCase(
        updateSchedule.fulfilled,
        (state, action: PayloadAction<Schedule>) => {
          const index = state.schedules.findIndex(
            (schedule) => schedule.id === action.payload.id
          );
          if (index !== -1) {
            state.schedules[index] = action.payload;
          }
        }
      )
      .addCase(
        bulkCreateSchedules.fulfilled,
        (state, action: PayloadAction<Schedule[]>) => {
          state.schedules = [...state.schedules, ...action.payload];
          state.totalSchedules += action.payload.length;
        }
      )
      .addCase(
        bulkUpdateSchedules.fulfilled,
        (state, action: PayloadAction<Schedule[]>) => {
          action.payload.forEach((updatedSchedule) => {
            const index = state.schedules.findIndex(
              (schedule) => schedule.id === updatedSchedule.id
            );
            if (index !== -1) {
              state.schedules[index] = updatedSchedule;
            }
          });
        }
      )
      .addCase(
        bulkDeleteSchedules.fulfilled,
        (state, action: PayloadAction<number[]>) => {
          state.schedules = state.schedules.filter(
            (schedule) => !action.payload.includes(schedule.id)
          );
          state.totalSchedules -= action.payload.length;
        }
      )
      .addCase(
        deactivateSchedule.fulfilled,
        (state, action: PayloadAction<Schedule>) => {
          const index = state.schedules.findIndex(
            (schedule) => schedule.id === action.payload.id
          );
          if (index !== -1) {
            state.schedules[index] = action.payload;
          }
        }
      )
      .addCase(
        cancelScheduleDeactivation.fulfilled,
        (state, action: PayloadAction<Schedule>) => {
          const index = state.schedules.findIndex(
            (schedule) => schedule.id === action.payload.id
          );
          if (index !== -1) {
            state.schedules[index] = action.payload;
          }
        }
      )
      .addCase(
        recreateSchedule.fulfilled,
        (state, action: PayloadAction<Schedule>) => {
          const index = state.schedules.findIndex(
            (schedule) => schedule.id === action.payload.id
          );
          if (index !== -1) {
            state.schedules[index] = action.payload;
          }
        }
      )

      // Attendees
      .addCase(fetchAttendees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAttendees.fulfilled,
        (
          state,
          action: PayloadAction<{ attendees: Attendee[]; total: number }>
        ) => {
          state.attendees = action.payload.attendees;
          state.totalAttendees = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchAttendees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch attendees";
      })
      .addCase(fetchAttendeesForUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAttendeesForUser.fulfilled,
        (state, action: PayloadAction<Attendee[]>) => {
          state.attendees = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchAttendeesForUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch attendees";
      })
      .addCase(
        deleteAttendee.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.attendees = state.attendees.filter(
            (attendee) => attendee.id !== action.payload
          );
          state.totalAttendees -= 1;
        }
      )
      .addCase(
        createAttendee.fulfilled,
        (state, action: PayloadAction<Attendee>) => {
          state.attendees.push(action.payload);
          state.totalAttendees += 1;
        }
      )
      .addCase(
        updateAttendee.fulfilled,
        (state, action: PayloadAction<Attendee>) => {
          const index = state.attendees.findIndex(
            (attendee) => attendee.id === action.payload.id
          );
          if (index !== -1) {
            state.attendees[index] = action.payload;
          }
        }
      )
      .addCase(
        adjustAttendeeClasses.fulfilled,
        (state, action: PayloadAction<Attendee>) => {
          const index = state.attendees.findIndex(
            (attendee) => attendee.id === action.payload.id
          );
          if (index !== -1) {
            state.attendees[index] = action.payload;
          }
        }
      )
      .addCase(getAttendeesToSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAttendeesToSchedule.fulfilled, (state, action: any) => {
        state.attendees = action.payload;
        state.loading = false;
      })
      .addCase(getAttendeesToSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch attendees";
      })

      // Images
      .addCase(uploadClassImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        uploadClassImages.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            imageUrls: string[];
            classId: number;
          }>
        ) => {
          state.loading = false;
          [state.classes, state.classesList, state.archivedClasses].forEach(
            (collection) =>
              appendImagesToClassCollection(
                collection,
                action.payload.classId,
                action.payload.imageUrls
              )
          );
        }
      )
      .addCase(uploadClassImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to upload images";
      })
      .addCase(deleteClassImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteClassImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            classId: number;
            imageUrl: string;
          }>
        ) => {
          state.loading = false;
          [state.classes, state.classesList, state.archivedClasses].forEach(
            (collection) =>
              removeImageFromClassCollection(
                collection,
                action.payload.classId,
                action.payload.imageUrl
              )
          );
        }
      )
      .addCase(deleteClassImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete image";
      })
      .addCase(uploadTypeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        uploadTypeImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            imageUrl: string;
            typeId: number;
          }>
        ) => {
          state.loading = false;
          setImageInTypeCollection(
            state.types,
            action.payload.typeId,
            action.payload.imageUrl
          );
        }
      )
      .addCase(uploadTypeImage.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to upload classification image";
      })
      .addCase(deleteTypeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteTypeImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            typeId: number;
            imageUrl: string;
          }>
        ) => {
          state.loading = false;
          clearImageFromTypeCollection(
            state.types,
            action.payload.typeId,
            action.payload.imageUrl
          );
        }
      )
      .addCase(deleteTypeImage.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to delete classification image";
      })
      .addCase(uploadSubtypeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        uploadSubtypeImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            imageUrl: string;
            subtypeId: number;
          }>
        ) => {
          state.loading = false;
          setImageInSubtypeCollection(
            state.types,
            action.payload.subtypeId,
            action.payload.imageUrl
          );
        }
      )
      .addCase(uploadSubtypeImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to upload subtype image";
      })
      .addCase(deleteSubtypeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSubtypeImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            message: string;
            subtypeId: number;
            imageUrl: string;
          }>
        ) => {
          state.loading = false;
          clearImageFromSubtypeCollection(
            state.types,
            action.payload.subtypeId,
            action.payload.imageUrl
          );
        }
      )
      .addCase(deleteSubtypeImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete subtype image";
      })

      // Status
      .addCase(
        setClassInProgressOfCancellation.fulfilled,
        (state, action: PayloadAction<Class>) => {
          const index = state.classes.findIndex(
            (classItem) => classItem.id === action.payload.id
          );
          if (index !== -1) {
            state.classes[index] = action.payload;
          }
        }
      )
      .addCase(
        reactivateClass.fulfilled,
        (state, action: PayloadAction<Class>) => {
          const index = state.classes.findIndex(
            (classItem) => classItem.id === action.payload.id
          );
          if (index !== -1) {
            state.classes[index] = action.payload;
          }
        }
      )

      // Report
      .addCase(getClassReport.pending, (state: AdminState) => {
        state.loading = true;
      })
      .addCase(
        getClassReport.fulfilled,
        (state: AdminState, action: PayloadAction<any>) => {
          state.loading = false;
          state.reports.attendeesReports = action.payload;
        }
      )
      .addCase(getClassReport.rejected, (state: AdminState) => {
        state.loading = false;
      })
      .addCase(getInstructorClassHoursReport.pending, (state: AdminState) => {
        state.loading = true;
      })
      .addCase(
        getInstructorClassHoursReport.fulfilled,
        (state: AdminState, action: PayloadAction<any>) => {
          state.loading = false;
          state.reports.instructorClassHoursReport = action.payload;
        }
      )
      .addCase(getInstructorClassHoursReport.rejected, (state: AdminState) => {
        state.loading = false;
      })
      .addCase(getEnrollmentsReport.pending, (state: AdminState) => {
        state.loading = true;
      })
      .addCase(
        getEnrollmentsReport.fulfilled,
        (state: AdminState, action: PayloadAction<any>) => {
          state.loading = false;
          state.reports.enrollmentsReport = action.payload;
        }
      )
      .addCase(getEnrollmentsReport.rejected, (state: AdminState) => {
        state.loading = false;
      })

      // Archive Classes
      .addCase(archiveClass.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        archiveClass.fulfilled,
        (state, action: PayloadAction<Class>) => {
          state.loading = false;
          // Remove the archived class from regular classes
          state.classes = state.classes.filter(
            (cls) => cls.id !== action.payload.id
          );
          state.classesList = state.classesList.filter(
            (cls) => cls.id !== action.payload.id
          );
          state.totalClasses -= 1;
          state.totalClassesList -= 1;
          // Add to archived classes if not already there
          if (
            !state.archivedClasses.find((cls) => cls.id === action.payload.id)
          ) {
            state.archivedClasses.push(action.payload);
            state.totalArchivedClasses += 1;
          }
        }
      )
      .addCase(archiveClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to archive class";
      })
      .addCase(unarchiveClass.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        unarchiveClass.fulfilled,
        (state, action: PayloadAction<Class>) => {
          state.loading = false;
          // Remove from archived classes
          state.archivedClasses = state.archivedClasses.filter(
            (cls) => cls.id !== action.payload.id
          );
          state.totalArchivedClasses -= 1;
          // Add to regular classes if not already there
          if (!state.classes.find((cls) => cls.id === action.payload.id)) {
            state.classes.push(action.payload);
            state.totalClasses += 1;
          }
          if (!state.classesList.find((cls) => cls.id === action.payload.id)) {
            state.classesList.push(action.payload);
            state.totalClassesList += 1;
          }
        }
      )
      .addCase(unarchiveClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to unarchive class";
      })
      .addCase(fetchArchivedClasses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchArchivedClasses.fulfilled,
        (state, action: PayloadAction<{ classes: Class[]; total: number }>) => {
          state.archivedClasses = action.payload.classes;
          state.totalArchivedClasses = action.payload.total;
          state.loading = false;
        }
      )
      .addCase(fetchArchivedClasses.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch archived classes";
      })
      .addCase(getArchivedClass.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getArchivedClass.fulfilled,
        (state, action: PayloadAction<Class>) => {
          state.loading = false;
          // Update the archived class in the array if it exists
          const index = state.archivedClasses.findIndex(
            (cls) => cls.id === action.payload.id
          );
          if (index !== -1) {
            state.archivedClasses[index] = action.payload;
          }
        }
      )
      .addCase(getArchivedClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to get archived class";
      })

      // Price Management
      .addCase(fetchPrices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPrices.fulfilled,
        (state, action: PayloadAction<{ prices: Price[]; total: number }>) => {
          state.loading = false;
          state.prices = action.payload.prices;
        }
      )
      .addCase(fetchPrices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch prices";
      })
      .addCase(createPrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPrice.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.prices) {
          state.prices = [];
        }
        state.prices.push(action.payload);
      })
      .addCase(createPrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create price";
      })
      .addCase(updatePrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrice.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.prices) {
          state.prices = [];
        }

        if (action.payload.replacedPriceId) {
          state.prices = state.prices.filter(
            (p) => p.id !== action.payload.replacedPriceId
          );
          state.prices.push(action.payload);
        } else {
          const index = state.prices.findIndex(
            (p) => p.id === action.payload.id
          );
          if (index !== -1) {
            state.prices[index] = action.payload;
          }
        }
      })
      .addCase(updatePrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update price";
      })
      .addCase(deletePrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePrice.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.prices) {
          state.prices = [];
        }
        state.prices = state.prices.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete price";
      })
      // Payment History
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentHistory = action.payload.payments;
        state.totalPaymentHistory = action.payload.total;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch payment history";
      })
      // Payment Statistics
      .addCase(fetchPaymentStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentStatistics = action.payload;
      })
      .addCase(fetchPaymentStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch payment statistics";
      });
  },
});

export default adminSlice.reducer;

export const { updateUnEnroll } = adminSlice.actions;
